/**
 * Email verification screen (Req 22.4, 22.5).
 *
 * Submits a verification token to the Identity_Service and presents the outcome
 * as a User_Message:
 *   - success -> the `auth.verify.success.*` message (Req 22.4);
 *   - expired/malformed/mismatched -> the `auth.verify.expiredInvalid.*` message
 *     (Req 22.5).
 * A "request a new verification email" control is ALWAYS ENABLED so the user can
 * re-request a token regardless of the current outcome (Req 22.5 / Req 8.5).
 *
 * All user-facing text resolves through the i18n catalog via `useT` (Req 22.11).
 *
 * Boundary: the Identity_Service calls (`onVerify`, `onRequestNew`) are INJECTED
 * props, so the screen never imports backend runtime — only shared TYPES from
 * `@avos/foundation`. The host wires the real calls in; tests inject fakes.
 */
import { useState, type FormEvent, type ReactElement } from "react";
import { useT } from "../i18n/index.js";
import {
  UserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/**
 * Outcome of the injected token-verification call: `verified` shows the success
 * message (Req 22.4); anything else shows the expired/invalid message (Req 22.5).
 */
export type VerificationResult = "verified" | "expiredOrInvalid";

/** Props for {@link EmailVerificationScreen}. */
export interface EmailVerificationScreenProps {
  /**
   * The Identity_Service token-verification call, injected so the screen never
   * imports backend runtime (web boundary).
   */
  readonly onVerify?: (token: string) => Promise<VerificationResult>;
  /**
   * The Identity_Service "request a new verification email" call, injected.
   * Invoked by the always-enabled request-new control (Req 22.5).
   */
  readonly onRequestNew?: () => Promise<void>;
}

/** What message (if any) to show for the last verification attempt. */
type VerifyState =
  | { readonly kind: "idle" }
  | { readonly kind: "submitting" }
  | { readonly kind: "result"; readonly result: VerificationResult };

export default function EmailVerificationScreen({
  onVerify,
  onRequestNew,
}: EmailVerificationScreenProps = {}): ReactElement {
  const t = useT();

  const [token, setToken] = useState("");
  const [state, setState] = useState<VerifyState>({ kind: "idle" });
  const [requestedNew, setRequestedNew] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setState({ kind: "submitting" });
    // With no service wired (isolated render), treat as expired/invalid rather
    // than a false success; the host injects the real call.
    const result: VerificationResult =
      onVerify === undefined ? "expiredOrInvalid" : await onVerify(token);
    setState({ kind: "result", result });
  }

  async function handleRequestNew(): Promise<void> {
    if (onRequestNew !== undefined) {
      await onRequestNew();
    }
    setRequestedNew(true);
  }

  const outcomeMessage: UserMessageModel | null =
    state.kind === "result"
      ? state.result === "verified"
        ? {
            what: t("auth.verify.success.what"),
            why: t("auth.verify.success.why"),
            next: t("auth.verify.success.next"),
          }
        : {
            what: t("auth.verify.expiredInvalid.what"),
            why: t("auth.verify.expiredInvalid.why"),
            next: t("auth.verify.expiredInvalid.next"),
          }
      : null;

  const isVerified = state.kind === "result" && state.result === "verified";

  return (
    <section aria-labelledby="screen-heading" data-screen="verifyEmail">
      <h1 id="screen-heading">{t("auth.verify.title")}</h1>
      <p>{t("auth.verify.prompt")}</p>

      {outcomeMessage !== null ? (
        <UserMessage
          message={outcomeMessage}
          severity={isVerified ? "status" : "error"}
        />
      ) : null}

      <form onSubmit={handleSubmit} noValidate data-testid="verify-form">
        <div className="field">
          <label htmlFor="verify-token">{t("auth.verify.tokenLabel")}</label>
          <input
            id="verify-token"
            name="token"
            type="text"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
        </div>

        <button type="submit" disabled={state.kind === "submitting"}>
          {t("auth.verify.submit")}
        </button>
      </form>

      {/*
        The "request a new verification email" control is ALWAYS ENABLED (never
        `disabled`) so the user can re-request a token at any time (Req 22.5).
      */}
      <button
        type="button"
        data-testid="verify-request-new"
        onClick={handleRequestNew}
      >
        {t("auth.verify.requestNew")}
      </button>
      {requestedNew ? (
        <UserMessage
          message={{
            what: t("auth.register.verifyEmailSent.what"),
            why: t("auth.register.verifyEmailSent.why"),
            next: t("auth.register.verifyEmailSent.next"),
          }}
          severity="status"
        />
      ) : null}
    </section>
  );
}
